import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { forwardRef, Injectable, EventEmitter, booleanAttribute, numberAttribute, Component, ChangeDetectionStrategy, Input, Output, ViewChild, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import * as i3 from 'primeng/autofocus';
import { AutoFocusModule } from 'primeng/autofocus';
import * as i1 from 'primeng/api';
const _c0 = ["input"];
const _c1 = (a0, a1, a2, a3) => ({
  "p-radiobutton p-component": true,
  "p-radiobutton-checked": a0,
  "p-radiobutton-disabled": a1,
  "p-radiobutton-focused": a2,
  "p-variant-filled": a3
});
const _c2 = (a0, a1, a2) => ({
  "p-radiobutton-box": true,
  "p-highlight": a0,
  "p-disabled": a1,
  "p-focus": a2
});
const _c3 = (a0, a1, a2) => ({
  "p-radiobutton-label": true,
  "p-radiobutton-label-active": a0,
  "p-disabled": a1,
  "p-radiobutton-label-focus": a2
});
function RadioButton_label_6_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "label", 7);
    i0.ɵɵlistener("click", function RadioButton_label_6_Template_label_click_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r3 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r3.select($event));
    });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    const input_r2 = i0.ɵɵreference(3);
    i0.ɵɵclassMap(ctx_r3.labelStyleClass);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction3(6, _c3, input_r2.checked, ctx_r3.disabled, ctx_r3.focused));
    i0.ɵɵattribute("for", ctx_r3.inputId)("data-pc-section", "label");
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r3.label);
  }
}
const RADIO_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RadioButton),
  multi: true
};
let RadioControlRegistry = /*#__PURE__*/(() => {
  class RadioControlRegistry {
    accessors = [];
    add(control, accessor) {
      this.accessors.push([control, accessor]);
    }
    remove(accessor) {
      this.accessors = this.accessors.filter(c => {
        return c[1] !== accessor;
      });
    }
    select(accessor) {
      this.accessors.forEach(c => {
        if (this.isSameGroup(c, accessor) && c[1] !== accessor) {
          c[1].writeValue(accessor.value);
        }
      });
    }
    isSameGroup(controlPair, accessor) {
      if (!controlPair[0].control) {
        return false;
      }
      return controlPair[0].control.root === accessor.control.control.root && controlPair[1].name === accessor.name;
    }
    static ɵfac = function RadioControlRegistry_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || RadioControlRegistry)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: RadioControlRegistry,
      factory: RadioControlRegistry.ɵfac,
      providedIn: 'root'
    });
  }
  return RadioControlRegistry;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * RadioButton is an extension to standard radio button element with theming.
 * @group Components
 */
let RadioButton = /*#__PURE__*/(() => {
  class RadioButton {
    cd;
    injector;
    registry;
    config;
    /**
     * Value of the radiobutton.
     * @group Props
     */
    value;
    /**
     * The name of the form control.
     * @group Props
     */
    formControlName;
    /**
     * Name of the radiobutton group.
     * @group Props
     */
    name;
    /**
     * When present, it specifies that the element should be disabled.
     * @group Props
     */
    disabled;
    /**
     * Label of the radiobutton.
     * @group Props
     */
    label;
    /**
     * Specifies the input variant of the component.
     * @group Props
     */
    variant = 'outlined';
    /**
     * Index of the element in tabbing order.
     * @group Props
     */
    tabindex;
    /**
     * Identifier of the focus input to match a label defined for the component.
     * @group Props
     */
    inputId;
    /**
     * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
     * @group Props
     */
    ariaLabelledBy;
    /**
     * Used to define a string that labels the input element.
     * @group Props
     */
    ariaLabel;
    /**
     * Inline style of the component.
     * @group Props
     */
    style;
    /**
     * Style class of the component.
     * @group Props
     */
    styleClass;
    /**
     * Style class of the label.
     * @group Props
     */
    labelStyleClass;
    /**
     * When present, it specifies that the component should automatically get focus on load.
     * @group Props
     */
    autofocus;
    /**
     * Callback to invoke on radio button click.
     * @param {RadioButtonClickEvent} event - Custom click event.
     * @group Emits
     */
    onClick = new EventEmitter();
    /**
     * Callback to invoke when the receives focus.
     * @param {Event} event - Browser event.
     * @group Emits
     */
    onFocus = new EventEmitter();
    /**
     * Callback to invoke when the loses focus.
     * @param {Event} event - Browser event.
     * @group Emits
     */
    onBlur = new EventEmitter();
    inputViewChild;
    onModelChange = () => {};
    onModelTouched = () => {};
    checked;
    focused;
    control;
    constructor(cd, injector, registry, config) {
      this.cd = cd;
      this.injector = injector;
      this.registry = registry;
      this.config = config;
    }
    ngOnInit() {
      this.control = this.injector.get(NgControl);
      this.checkName();
      this.registry.add(this.control, this);
    }
    handleClick(event, radioButton, focus) {
      if (this.disabled) {
        return;
      }
      this.select(event);
      if (focus) {
        radioButton.focus();
      }
    }
    select(event) {
      if (!this.disabled) {
        this.inputViewChild.nativeElement.checked = true;
        this.checked = true;
        this.onModelChange(this.value);
        this.registry.select(this);
        this.onClick.emit({
          originalEvent: event,
          value: this.value
        });
      }
    }
    writeValue(value) {
      this.checked = value == this.value;
      if (this.inputViewChild && this.inputViewChild.nativeElement) {
        this.inputViewChild.nativeElement.checked = this.checked;
      }
      this.cd.markForCheck();
    }
    registerOnChange(fn) {
      this.onModelChange = fn;
    }
    registerOnTouched(fn) {
      this.onModelTouched = fn;
    }
    setDisabledState(val) {
      this.disabled = val;
      this.cd.markForCheck();
    }
    onInputFocus(event) {
      this.focused = true;
      this.onFocus.emit(event);
    }
    onInputBlur(event) {
      this.focused = false;
      this.onModelTouched();
      this.onBlur.emit(event);
    }
    /**
     * Applies focus to input field.
     * @group Method
     */
    focus() {
      this.inputViewChild.nativeElement.focus();
    }
    ngOnDestroy() {
      this.registry.remove(this);
    }
    checkName() {
      if (this.name && this.formControlName && this.name !== this.formControlName) {
        this.throwNameError();
      }
      if (!this.name && this.formControlName) {
        this.name = this.formControlName;
      }
    }
    throwNameError() {
      throw new Error(`
          If you define both a name and a formControlName attribute on your radio button, their values
          must match. Ex: <p-radioButton formControlName="food" name="food"></p-radioButton>
        `);
    }
    static ɵfac = function RadioButton_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || RadioButton)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(RadioControlRegistry), i0.ɵɵdirectiveInject(i1.PrimeNGConfig));
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: RadioButton,
      selectors: [["p-radioButton"]],
      viewQuery: function RadioButton_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputViewChild = _t.first);
        }
      },
      hostAttrs: [1, "p-element"],
      inputs: {
        value: "value",
        formControlName: "formControlName",
        name: "name",
        disabled: [2, "disabled", "disabled", booleanAttribute],
        label: "label",
        variant: "variant",
        tabindex: [2, "tabindex", "tabindex", numberAttribute],
        inputId: "inputId",
        ariaLabelledBy: "ariaLabelledBy",
        ariaLabel: "ariaLabel",
        style: "style",
        styleClass: "styleClass",
        labelStyleClass: "labelStyleClass",
        autofocus: [2, "autofocus", "autofocus", booleanAttribute]
      },
      outputs: {
        onClick: "onClick",
        onFocus: "onFocus",
        onBlur: "onBlur"
      },
      features: [i0.ɵɵProvidersFeature([RADIO_VALUE_ACCESSOR]), i0.ɵɵInputTransformsFeature],
      decls: 7,
      vars: 30,
      consts: [["input", ""], [3, "click", "ngStyle", "ngClass"], [1, "p-hidden-accessible"], ["type", "radio", "pAutoFocus", "", 3, "focus", "blur", "checked", "disabled", "value", "autofocus"], [3, "ngClass"], [1, "p-radiobutton-icon"], [3, "class", "ngClass", "click", 4, "ngIf"], [3, "click", "ngClass"]],
      template: function RadioButton_Template(rf, ctx) {
        if (rf & 1) {
          const _r1 = i0.ɵɵgetCurrentView();
          i0.ɵɵelementStart(0, "div", 1);
          i0.ɵɵlistener("click", function RadioButton_Template_div_click_0_listener($event) {
            i0.ɵɵrestoreView(_r1);
            const input_r2 = i0.ɵɵreference(3);
            return i0.ɵɵresetView(ctx.handleClick($event, input_r2, true));
          });
          i0.ɵɵelementStart(1, "div", 2)(2, "input", 3, 0);
          i0.ɵɵlistener("focus", function RadioButton_Template_input_focus_2_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.onInputFocus($event));
          })("blur", function RadioButton_Template_input_blur_2_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.onInputBlur($event));
          });
          i0.ɵɵelementEnd()();
          i0.ɵɵelementStart(4, "div", 4);
          i0.ɵɵelement(5, "span", 5);
          i0.ɵɵelementEnd()();
          i0.ɵɵtemplate(6, RadioButton_label_6_Template, 2, 10, "label", 6);
        }
        if (rf & 2) {
          i0.ɵɵclassMap(ctx.styleClass);
          i0.ɵɵproperty("ngStyle", ctx.style)("ngClass", i0.ɵɵpureFunction4(21, _c1, ctx.checked, ctx.disabled, ctx.focused, ctx.variant === "filled" || ctx.config.inputStyle() === "filled"));
          i0.ɵɵattribute("data-pc-name", "radiobutton")("data-pc-section", "root");
          i0.ɵɵadvance();
          i0.ɵɵattribute("data-pc-section", "hiddenInputWrapper");
          i0.ɵɵadvance();
          i0.ɵɵproperty("checked", ctx.checked)("disabled", ctx.disabled)("value", ctx.value)("autofocus", ctx.autofocus);
          i0.ɵɵattribute("id", ctx.inputId)("name", ctx.name)("aria-labelledby", ctx.ariaLabelledBy)("aria-label", ctx.ariaLabel)("tabindex", ctx.tabindex)("data-pc-section", "hiddenInput");
          i0.ɵɵadvance(2);
          i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction3(26, _c2, ctx.checked, ctx.disabled, ctx.focused));
          i0.ɵɵattribute("data-pc-section", "input");
          i0.ɵɵadvance();
          i0.ɵɵattribute("data-pc-section", "icon");
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", ctx.label);
        }
      },
      dependencies: [i2.NgClass, i2.NgIf, i2.NgStyle, i3.AutoFocus],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return RadioButton;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RadioButtonModule = /*#__PURE__*/(() => {
  class RadioButtonModule {
    static ɵfac = function RadioButtonModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || RadioButtonModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: RadioButtonModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, AutoFocusModule]
    });
  }
  return RadioButtonModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { RADIO_VALUE_ACCESSOR, RadioButton, RadioButtonModule, RadioControlRegistry };
